import { ft } from '../core/units.js';
import { parseTime } from './clock.js';
import { SEARCHES } from '../data/searches.js';

/**
 * Looking for somebody who is not where they are meant to be.
 *
 * Three things, and the searching is none of them — the searching is the
 * player walking the building, asking people and watching where people go:
 *
 *   who has seen them   anybody with eyes who has had the one being looked
 *                       for in view remembers where, when, and which way they
 *                       were going. Asked by the same `sees` that decides
 *                       whether anybody has seen the player, pointed at a
 *                       colleague instead
 *   what they say       while you are looking, somebody you walk up to tells
 *                       you what they saw, as what they have to say to you.
 *                       Nobody is a hint system; they are people who were
 *                       about
 *   finding them        talking to them. What they say then is on their row,
 *                       said the way anybody's greeting is said. Nothing here
 *                       adds a word to it
 *
 * Nothing moves the one being looked for until they are found. Where they are
 * is their route.
 */

/** Seconds between looks. Somebody glancing up, not a camera. */
const LOOK_EVERY = 0.25;

/** Feet ahead of somebody walking, to say which way they were going. */
const AHEAD = 20;

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

/** A line from the row, with the blanks filled. */
const fill = (text, values) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');

export function createSearches({ npcs, level, clock, standing, hud, rows = SEARCHES }) {
  /** The errand on now: which, on which day, and whether they have been found. */
  let active = null;
  /** How each finished, for the record. */
  let records = [];
  /** Per search, per witness: where and when they last had them in view. */
  let seen = {};
  const was = new Map();
  let sinceLook = 0;

  const soughtFor = (row) => npcs.find((npc) => npc.data.sought === row.id) ?? null;
  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;
  const roomName = (x, z) => level.roomAt(x, z)?.name ?? null;
  const firstName = (npc) => npc.data.name.split(' ')[0];

  /** Where somebody is heading, from where they were a moment ago. */
  function heading(npc) {
    const before = was.get(npc.data.id);
    if (!before) return null;
    const dx = npc.position.x - before.x;
    const dz = npc.position.z - before.z;
    const moved = Math.hypot(dx, dz);
    if (moved < 0.05) return null;
    const x = npc.position.x + (dx / moved) * ft(AHEAD);
    const z = npc.position.z + (dz / moved) * ft(AHEAD);
    const ahead = roomName(x, z);
    return ahead && ahead !== roomName(npc.position.x, npc.position.z) ? ahead : null;
  }

  function look() {
    for (const row of rows) {
      const target = soughtFor(row);
      if (!target || !target.present) continue;
      const going = heading(target);
      const room = roomName(target.position.x, target.position.z);
      for (const npc of npcs) {
        if (npc === target || !npc.present || !npc.hasVision) continue;
        if (!npc.sees({ x: target.position.x, y: 1.2, z: target.position.z })) continue;
        seen[row.id] ??= {};
        seen[row.id][npc.data.id] = { room, going, at: clockOf(clock) };
      }
      was.set(target.data.id, { x: target.position.x, z: target.position.z });
    }
  }

  function ago(minutes) {
    if (minutes < 3) return 'just now';
    if (minutes < 60) return `about ${Math.max(5, Math.round(minutes / 5) * 5)} minutes ago`;
    const hours = Math.round(minutes / 60);
    return `about ${hours === 1 ? 'an hour' : `${hours} hours`} ago`;
  }

  function close(outcome) {
    const row = rowOf(active.id);
    records.push({ id: active.id, day: active.day, outcome, at: clock.minutes });
    const target = row ? soughtFor(row) : null;
    if (target?.posted) target.unpost();
    active = null;
  }

  return {
    rows,

    /** Somebody wants somebody found. From a beat, when it is played. */
    post(id) {
      if (!rowOf(id)) return;
      active = { id, day: clock.day, found: false };
    },

    get active() {
      return active ? { ...active } : null;
    },

    get records() {
      return records.map((record) => ({ ...record }));
    },

    /** Who has seen them, for a check. */
    sightings(id) {
      return { ...(seen[id] ?? {}) };
    },

    update(dt = 1 / 60) {
      sinceLook += dt;
      if (sinceLook >= LOOK_EVERY) {
        sinceLook = 0;
        look();
      }
      if (!active) return;
      const row = rowOf(active.id);
      // Gone home, or it is another day: whoever wanted them has stopped asking.
      if (clock.day !== active.day || clock.minutes >= parseTime(row.until)) {
        close(active.found ? 'found' : 'lapsed');
      }
    },

    /**
     * What somebody says when you walk up to them while you are looking, or
     * null for whatever they would have said anyway.
     */
    speaks(npc) {
      if (!active) return null;
      const row = rowOf(active.id);
      const target = soughtFor(row);
      if (!target) return null;
      if (npc === target) return active.found ? target.data.says?.found ?? null : null;
      if (active.found || !npc.data.department || npc.data.department === 'guests') return null;
      const name = firstName(target);
      const saw = seen[row.id]?.[npc.data.id];
      const minutes = saw ? clockOf(clock) - saw.at : Infinity;
      if (!saw || minutes > row.recent) return fill(row.asked.unseen, { name });
      return fill(row.asked.seen, {
        name,
        where: saw.room ? `in the ${saw.room}` : 'about',
        ago: ago(minutes),
        going: saw.going ? `, going into the ${saw.going}` : '',
      });
    },

    /** You walked up to somebody and they turned to you. */
    met(npc) {
      if (!active || active.found) return;
      const row = rowOf(active.id);
      const target = soughtFor(row);
      if (npc !== target) return;
      active.found = true;
      // Into the phone, as you come round the door, and then it is away.
      if (target.data.says?.phone) target.say(target.data.says.phone, 2.5);
      standing?.applyAll(row.award ?? {}, { reason: `Found ${firstName(target)}` });
      if (row.then?.length) {
        target.post(
          row.then.map(([x, z], index) => ({ x: ft(x), z: ft(z), dwell: index === row.then.length - 1 ? 1e6 : 0 }))
        );
      }
      if (row.found) hud?.say(fill(row.found, { name: firstName(target) }), { tone: 'info', seconds: 3 });
    },

    toJSON: () => ({ active, records, seen }),
    load(state) {
      active = state?.active ? { ...state.active } : null;
      records = (state?.records ?? []).map((record) => ({ ...record }));
      seen = JSON.parse(JSON.stringify(state?.seen ?? {}));
      was.clear();
    },
    clear() {
      active = null;
      records = [];
      seen = {};
      was.clear();
    },
  };
}
